import { linkOptions } from '@tanstack/react-router'

/**
 * Every org-scoped URL in the app is built here.
 *
 * These are `linkOptions`, not strings, and that is the point. A destination is
 * `{ to, params, search }` — `to` is a route PATTERN checked against the
 * generated route tree, and the params are checked against that pattern. A
 * renamed segment or a missing param is a type error at every call site.
 *
 * The previous version of this file returned interpolated strings cast to
 * Next's `Route` type, and existed mainly to contain those casts: `typedRoutes`
 * could only verify string *literals*, so `/o/${org}/qr-codes` was unverifiable
 * — `string` might contain a slash — and every builder ended in `as Route`,
 * which asserts rather than checks. A broken destination compiled cleanly and
 * 404'd in front of a customer. Nothing here asserts any more.
 *
 * Spread them at the call site: `<Link {...r.qrCodes} />`,
 * `navigate({ ...r.qrCode(id) })`, `redirect({ ...r.archive })`.
 */
export function orgRoutes(org: string) {
  return {
    qrCodes: linkOptions({ to: '/o/$org/qr-codes', params: { org } }),
    qrCode: (id: string) =>
      linkOptions({ to: '/o/$org/qr-codes/$id', params: { org, id } }),
    /**
     * A code opened *from* a group. The `from` param is what lets the
     * breadcrumb show the path you actually walked and the sidebar highlight
     * one row instead of two — the same code can be reached down several
     * branches, so the route has to carry which one you took.
     */
    qrCodeFrom: (id: string, groupId: string) =>
      linkOptions({
        to: '/o/$org/qr-codes/$id',
        params: { org, id },
        search: { from: groupId },
      }),
    qrCodeAnalytics: (id: string) =>
      linkOptions({ to: '/o/$org/qr-codes/$id/analytics', params: { org, id } }),
    qrCodeScans: (id: string) =>
      linkOptions({ to: '/o/$org/qr-codes/$id/scans', params: { org, id } }),
    qrCodeSchedules: (id: string) =>
      linkOptions({ to: '/o/$org/qr-codes/$id/schedules', params: { org, id } }),
    /** The org-wide analytics dashboard — every code in aggregate. */
    analytics: linkOptions({ to: '/o/$org/analytics', params: { org } }),
    /** A saved view is a routable report — its uuid is a path segment. */
    analyticsView: (viewId: string) =>
      linkOptions({
        to: '/o/$org/analytics/$viewId',
        params: { org, viewId },
      }),
    /** Scanner identities — the cookie profiles the redirector builds. */
    identities: linkOptions({ to: '/o/$org/identities', params: { org } }),
    identity: (id: string) =>
      linkOptions({ to: '/o/$org/identities/$id', params: { org, id } }),
    /* The identity's module tabs — routes, so each is linkable. */
    identityEngagement: (id: string) =>
      linkOptions({ to: '/o/$org/identities/$id/engagement', params: { org, id } }),
    identityRecord: (id: string) =>
      linkOptions({ to: '/o/$org/identities/$id/identity', params: { org, id } }),
    groups: linkOptions({ to: '/o/$org/groups', params: { org } }),
    group: (id: string) => linkOptions({ to: '/o/$org/groups/$id', params: { org, id } }),
    archive: linkOptions({ to: '/o/$org/archive', params: { org } }),
    members: linkOptions({ to: '/o/$org/members', params: { org } }),
    billing: linkOptions({ to: '/o/$org/billing', params: { org } }),
    /** Platform administration — administrators only see the link. */
    admin: linkOptions({ to: '/o/$org/admin', params: { org } }),
    /** Which build of the app each user is running. */
    adminVersions: linkOptions({ to: '/o/$org/admin/versions', params: { org } }),
  }
}

export type OrgRoutes = ReturnType<typeof orgRoutes>

type Resolved<T> = T extends (...args: never[]) => infer R ? R : T

/**
 * Any one destination this module can name.
 *
 * The union of every value above, with the builders resolved to what they
 * return — so a function that produces "a place to go" can say so without
 * widening to `string` and giving up the checking this file exists for.
 */
export type OrgLinkTarget = Resolved<OrgRoutes[keyof OrgRoutes]>
